// Builds the structured OnboardingTeacherQuestionnaire POSTed to
// media/{org}/onboarding-questionnaire. The backend converts the filled
// questionnaire into an org-scoped Creator Profile (and computes the DNA).
//
// The wizard's answer codes already match the backend answer enums 1:1
// (voice1/voice2/illustration/structure), so mapping is a near-identity. When
// the server template is available we reuse its question text + slider bounds;
// otherwise we fall back to sensible defaults.

import type {
  FreeTextQuestion,
  OnboardingTeacherQuestionnaire,
  SliderQuestion,
} from "../creator-profile/types";
import type { TeacherAnswers } from "./types";

const freeText = (
  templateQuestion: FreeTextQuestion | undefined,
  answer: string | null
): FreeTextQuestion => ({
  question: templateQuestion?.question ?? "",
  answer: answer && answer.length > 0 ? answer : null,
});

const slider = (
  templateQuestion: SliderQuestion | undefined,
  answer: number
): SliderQuestion => ({
  question: templateQuestion?.question ?? "",
  answer,
  min_value: templateQuestion?.min_value ?? 0,
  max_value: templateQuestion?.max_value ?? 100,
  step: templateQuestion?.step ?? 1,
});

export const buildTeacherQuestionnaire = (
  answers: TeacherAnswers,
  template?: OnboardingTeacherQuestionnaire
): OnboardingTeacherQuestionnaire => ({
  type: "teacher",
  // discipline/subject are not collected by the current wizard steps.
  discipline: freeText(template?.discipline, null),
  subject: freeText(template?.subject, null),
  voice1: {
    question: template?.voice1.question ?? "",
    answer: answers.voice1,
  },
  voice2: {
    question: template?.voice2.question ?? "",
    answer: answers.voice2,
  },
  illustration: {
    question: template?.illustration.question ?? "",
    answer: answers.illustration,
  },
  structure: {
    question: template?.structure.question ?? "",
    answer: answers.structure,
  },
  depth: slider(template?.depth, answers.depth),
  autonomy: slider(template?.autonomy, answers.autonomy),
  guardrail: freeText(template?.guardrail, answers.guardrail.trim()),
});
